import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Button,
  Input,
  Select,
  Popconfirm,
  Tag,
  Tooltip,
  Spin,
} from "antd";
import {
  Search,
  Plus,
  Eye,
  Edit,
  Trash2,
  Users,
  RefreshCw,
  Mail,
  Phone,
  Calendar,
  Shield,
  FileText,
  UserCheck,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { passengerAdminApi } from "../../lib/passengerAdminApi";

const { Option } = Select;

const RAW_BASE =
  import.meta.env.VITE_URL ||
  import.meta.env.VITE_URL2 ||
  "http://localhost:3000";
const API_BASE = String(RAW_BASE).replace(/\/api\/?$/i, "");

const INITIAL_FORM = {
  userId: "",
  title: "Mr",
  firstName: "",
  lastName: "",
  gender: "Male",
  dateOfBirth: "",
  passengerType: "Adult",
  panNumber: "",
  passportNumber: "",
  passportIssueDate: "",
  passportExpiryDate: "",
  passportIssueCountry: "",
  nationality: "India",
  mobileNumber: "",
  emailAddress: "",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const formatDateOnlyForInput = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

export default function AdminPassengers() {
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  // Users list for dropdown in Create modal
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);

  const fetchPassengers = async () => {
    try {
      setLoading(true);
      const res = await passengerAdminApi.getAllPassengers();
      if (res?.success && Array.isArray(res.data)) {
        setPassengers(res.data);
      } else {
        setPassengers([]);
      }
    } catch (err) {
      console.error("Failed to fetch admin passengers:", err);
      toast.error(err?.response?.data?.message || "Failed to load passengers");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await axios.get(`${API_BASE}/api/user/?limit=1000`, {
        withCredentials: true,
      });
      if (res.data?.data && Array.isArray(res.data.data)) {
        setUsersList(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load users list for dropdown:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchPassengers();
    fetchUsers();
  }, []);

  const handleOpenCreateModal = () => {
    setModalMode("create");
    setSelectedId(null);
    setFormData(INITIAL_FORM);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setModalMode("edit");
    setSelectedId(record._id);
    setFormData({
      userId: record.user?._id || record.user || "",
      title: record.title || "Mr",
      firstName: record.firstName || "",
      lastName: record.lastName || "",
      gender: record.gender || "Male",
      dateOfBirth: formatDateOnlyForInput(record.dateOfBirth),
      passengerType: record.passengerType || "Adult",
      panNumber: record.panNumber || "",
      passportNumber: record.passportNumber || "",
      passportIssueDate: formatDateOnlyForInput(record.passportIssueDate),
      passportExpiryDate: formatDateOnlyForInput(record.passportExpiryDate),
      passportIssueCountry: record.passportIssueCountry || "",
      nationality: record.nationality || "India",
      mobileNumber: record.mobileNumber || "",
      emailAddress: record.emailAddress || "",
    });
    setIsModalOpen(true);
  };

  const handleOpenDetailModal = (record) => {
    setSelectedPassenger(record);
    setDetailModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "panNumber" || name === "passportNumber" || name === "firstName" || name === "lastName"
          ? value.toUpperCase()
          : value,
    }));
  };

  const handleSubmitModal = async (e) => {
    e.preventDefault();

    if (modalMode === "create" && !formData.userId) {
      toast.error("Please select or enter the User ID for this passenger");
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error("First Name and Last Name are required");
      return;
    }

    if (!formData.dateOfBirth) {
      toast.error("Date of Birth is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        title: formData.title,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        passengerType: formData.passengerType,
        nationality: formData.nationality.trim() || "India",
        panNumber: formData.panNumber.trim().toUpperCase() || undefined,
        passportNumber:
          formData.passportNumber.trim().toUpperCase() || undefined,
        passportIssueDate: formData.passportIssueDate || undefined,
        passportExpiryDate: formData.passportExpiryDate || undefined,
        mobileNumber: formData.mobileNumber.trim() || undefined,
        emailAddress: formData.emailAddress.trim().toLowerCase() || undefined,
      };

      if (modalMode === "create") {
        payload.userId = formData.userId;
        const res = await passengerAdminApi.createPassenger(payload);
        if (res?.success) {
          toast.success("Passenger created successfully by Admin!");
          setIsModalOpen(false);
          await fetchPassengers();
        } else {
          toast.error(res?.message || "Failed to create passenger");
        }
      } else {
        const res = await passengerAdminApi.updatePassenger(selectedId, payload);
        if (res?.success) {
          toast.success("Passenger updated successfully!");
          setIsModalOpen(false);
          await fetchPassengers();
        } else {
          toast.error(res?.message || "Failed to update passenger");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Error saving passenger details",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await passengerAdminApi.deletePassenger(id);
      if (res?.success) {
        toast.success("Passenger deleted successfully");
        await fetchPassengers();
      } else {
        toast.error(res?.message || "Failed to delete passenger");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Error deleting passenger");
    }
  };

  // Filter passengers
  const filteredPassengers = passengers.filter((pax) => {
    const fullName =
      `${pax.firstName || ""} ${pax.lastName || ""}`.toLowerCase();
    const userName = (pax.user?.username || pax.user?.name || "").toLowerCase();
    const userEmail = pax.user?.email?.toLowerCase() || "";
    const search = searchTerm.toLowerCase().trim();

    const matchesSearch =
      !search ||
      fullName.includes(search) ||
      userName.includes(search) ||
      userEmail.includes(search) ||
      pax.emailAddress?.toLowerCase().includes(search) ||
      pax.mobileNumber?.includes(search) ||
      pax.passportNumber?.toLowerCase().includes(search) ||
      pax.panNumber?.toLowerCase().includes(search);

    const matchesType =
      typeFilter === "All" ||
      pax.passengerType?.toLowerCase() === typeFilter.toLowerCase();

    return matchesSearch && matchesType;
  });

  const columns = [
    {
      title: "Passenger",
      key: "passenger",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0 text-xs">
            {record.firstName ? record.firstName.charAt(0).toUpperCase() : "P"}
            {record.lastName ? record.lastName.charAt(0).toUpperCase() : ""}
          </div>
          <div>
            <span className="font-bold text-gray-900 block text-sm leading-tight">
              {record.title || "Mr"} {record.firstName} {record.lastName}
            </span>
            <span className="text-xs text-gray-500">
              {record.gender || "Male"} • {record.nationality || "India"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "User Account",
      key: "user",
      render: (_, record) => {
        if (!record.user) {
          return <span className="text-xs text-gray-400">Unassigned</span>;
        }
        if (typeof record.user === "object") {
          return (
            <div>
              <span className="font-semibold text-gray-800 block text-xs">
                {record.user.username || record.user.name || "User"}
              </span>
              <span className="text-[11px] text-gray-500">
                {record.user.email || "-"}
              </span>
            </div>
          );
        }
        return (
          <span className="font-mono text-xs text-gray-600">
            {String(record.user)}
          </span>
        );
      },
    },
    {
      title: "Type & DOB",
      key: "type",
      render: (_, record) => {
        const typeColor =
          record.passengerType === "Adult"
            ? "blue"
            : record.passengerType === "Child"
              ? "green"
              : "purple";
        return (
          <div>
            <Tag color={typeColor} className="font-semibold text-[11px]">
              {record.passengerType || "Adult"}
            </Tag>
            <span className="text-xs text-gray-500 block mt-0.5">
              {formatDate(record.dateOfBirth)}
            </span>
          </div>
        );
      },
    },
    {
      title: "Documents",
      key: "documents",
      render: (_, record) => (
        <div className="space-y-0.5 text-xs">
          <div>
            <span className="text-gray-400 text-[10px] uppercase font-bold">
              Passport:{" "}
            </span>
            <span className="font-mono font-bold text-gray-800">
              {record.passportNumber || "-"}
            </span>
          </div>
          <div>
            <span className="text-gray-400 text-[10px] uppercase font-bold">
              PAN:{" "}
            </span>
            <span className="font-mono text-gray-700">
              {record.panNumber || "-"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => (
        <div className="space-y-0.5 text-xs">
          {record.mobileNumber && (
            <div className="flex items-center gap-1 text-gray-700">
              <Phone size={11} className="text-blue-600" />
              <span>{record.mobileNumber}</span>
            </div>
          )}
          {record.emailAddress && (
            <div className="flex items-center gap-1 text-gray-500 truncate max-w-[150px]">
              <Mail size={11} className="text-blue-600 shrink-0" />
              <span className="truncate">{record.emailAddress}</span>
            </div>
          )}
          {!record.mobileNumber && !record.emailAddress && (
            <span className="text-gray-400 text-xs">-</span>
          )}
        </div>
      ),
    },
    {
      title: "Created",
      key: "createdAt",
      render: (_, record) => (
        <span className="text-xs text-gray-500">
          {formatDate(record.createdAt)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <div className="flex items-center justify-end gap-1.5">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<Eye size={14} className="text-blue-600" />}
              onClick={() => handleOpenDetailModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Passenger">
            <Button
              type="text"
              size="small"
              icon={<Edit size={14} className="text-amber-600" />}
              onClick={() => handleOpenEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Passenger"
            description="Are you sure you want to delete this passenger?"
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record._id)}
          >
            <Tooltip title="Delete Passenger">
              <Button
                type="text"
                size="small"
                danger
                icon={<Trash2 size={14} />}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const totalAdults = passengers.filter(
    (p) => p.passengerType === "Adult",
  ).length;
  const totalChildren = passengers.filter(
    (p) => p.passengerType === "Child",
  ).length;
  const totalInfants = passengers.filter(
    (p) => p.passengerType === "Infant",
  ).length;
  const withPassports = passengers.filter(
    (p) => Boolean(p.passportNumber),
  ).length;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Passenger Management
            </h1>
            <Tag color="blue" className="font-bold">
              {passengers.length} Records
            </Tag>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            View, create, update, and manage travel passengers across all user
            accounts
          </p>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto">
          <Button
            icon={<RefreshCw size={14} />}
            onClick={fetchPassengers}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={handleOpenCreateModal}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Add Passenger for User
          </Button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <span className="text-xs text-gray-500 font-medium">
            Total Passengers
          </span>
          <p className="text-2xl font-black text-gray-900 mt-0.5">
            {passengers.length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <span className="text-xs text-gray-500 font-medium">Adults</span>
          <p className="text-2xl font-black text-blue-600 mt-0.5">
            {totalAdults}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <span className="text-xs text-gray-500 font-medium">
            Children &amp; Infants
          </span>
          <p className="text-2xl font-black text-emerald-600 mt-0.5">
            {totalChildren + totalInfants}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <span className="text-xs text-gray-500 font-medium">
            Passports Logged
          </span>
          <p className="text-2xl font-black text-indigo-600 mt-0.5">
            {withPassports}
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="w-full sm:w-80">
          <Input
            prefix={<Search size={14} className="text-gray-400" />}
            placeholder="Search by passenger name, user email, passport, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs text-gray-500 font-medium">Type:</span>
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 140 }}
          >
            <Option value="All">All Types</Option>
            <Option value="Adult">Adults</Option>
            <Option value="Child">Children</Option>
            <Option value="Infant">Infants</Option>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <Table
          columns={columns}
          dataSource={filteredPassengers}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total) => `Total ${total} passengers`,
          }}
          scroll={{ x: 800 }}
        />
      </div>

      {/* CREATE / EDIT MODAL */}
      <Modal
        title={
          modalMode === "create"
            ? "Add Passenger for User"
            : "Edit Passenger Details"
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={650}
        destroyOnClose
      >
        <form onSubmit={handleSubmitModal} className="space-y-4 pt-2">
          {modalMode === "create" && (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Select User Account *
              </label>
              <Select
                showSearch
                placeholder="Search user by name or email"
                optionFilterProp="children"
                value={formData.userId || undefined}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, userId: val }))
                }
                loading={loadingUsers}
                className="w-full"
                filterOption={(input, option) => {
                  const children = option?.children;
                  const text = Array.isArray(children) ? children.join("") : (children ?? "");
                  return String(text).toLowerCase().includes((input || "").toLowerCase());
                }}
              >
                {usersList.map((u) => (
                  <Option key={u._id} value={u._id}>
                    {u.username || u.name} ({u.email})
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* Personal Info */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider block">
              Personal Information
            </span>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Title *
                </label>
                <select
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  className="w-full p-2 border rounded-lg text-xs"
                >
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                </select>
              </div>

              <div className="col-span-5">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  required
                  className="w-full p-2 border rounded-lg text-xs"
                  placeholder="First name"
                />
              </div>

              <div className="col-span-4">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  required
                  className="w-full p-2 border rounded-lg text-xs"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Gender *
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleFormChange}
                  className="w-full p-2 border rounded-lg text-xs"
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleFormChange}
                  required
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Passenger Type *
                </label>
                <select
                  name="passengerType"
                  value={formData.passengerType}
                  onChange={handleFormChange}
                  className="w-full p-2 border rounded-lg text-xs"
                >
                  <option value="Adult">Adult</option>
                  <option value="Child">Child</option>
                  <option value="Infant">Infant</option>
                </select>
              </div>
            </div>
          </div>

          {/* Travel Documents */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider block">
              Passport &amp; Documents
            </span>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  PAN Number
                </label>
                <input
                  type="text"
                  name="panNumber"
                  maxLength={10}
                  value={formData.panNumber}
                  onChange={handleFormChange}
                  placeholder="ABCDE1234F"
                  className="w-full p-2 border rounded-lg text-xs font-mono uppercase"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Nationality
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleFormChange}
                  placeholder="India"
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Passport Number
                </label>
                <input
                  type="text"
                  name="passportNumber"
                  value={formData.passportNumber}
                  onChange={handleFormChange}
                  placeholder="Passport Number"
                  className="w-full p-2 border rounded-lg text-xs font-mono uppercase"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Passport Issue Country
                </label>
                <input
                  type="text"
                  name="passportIssueCountry"
                  value={formData.passportIssueCountry}
                  onChange={handleFormChange}
                  placeholder="e.g. India"
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Passport Issue Date
                </label>
                <input
                  type="date"
                  name="passportIssueDate"
                  value={formData.passportIssueDate}
                  onChange={handleFormChange}
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Passport Expiry Date
                </label>
                <input
                  type="date"
                  name="passportExpiryDate"
                  value={formData.passportExpiryDate}
                  onChange={handleFormChange}
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider block">
              Contact Information
            </span>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobileNumber"
                  maxLength={10}
                  value={formData.mobileNumber}
                  onChange={handleFormChange}
                  placeholder="10-digit mobile number"
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleFormChange}
                  placeholder="traveler@example.com"
                  className="w-full p-2 border rounded-lg text-xs"
                />
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              className="bg-blue-600"
            >
              {modalMode === "create" ? "Create Passenger" : "Save Changes"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* DETAIL MODAL */}
      <Modal
        title="Passenger Full Profile"
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={550}
      >
        {selectedPassenger && (
          <div className="space-y-4 pt-2 text-xs">
            {/* Header info */}
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center text-sm">
                {selectedPassenger.firstName?.charAt(0)}
                {selectedPassenger.lastName?.charAt(0)}
              </div>
              <div>
                <h3 className="font-bold text-base text-gray-900">
                  {selectedPassenger.title} {selectedPassenger.firstName}{" "}
                  {selectedPassenger.lastName}
                </h3>
                <span className="text-gray-500">
                  {selectedPassenger.gender} • {selectedPassenger.passengerType}{" "}
                  • {selectedPassenger.nationality}
                </span>
              </div>
            </div>

            {/* Grid attributes */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">
                  Date of Birth
                </span>
                <span className="font-semibold text-gray-800">
                  {formatDate(selectedPassenger.dateOfBirth)}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">
                  PAN Number
                </span>
                <span className="font-mono font-bold text-gray-800">
                  {selectedPassenger.panNumber || "-"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">
                  Passport Number
                </span>
                <span className="font-mono font-bold text-gray-800">
                  {selectedPassenger.passportNumber || "-"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">
                  Passport Expiry
                </span>
                <span className="font-semibold text-gray-800">
                  {formatDate(selectedPassenger.passportExpiryDate)}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">
                  Contact Phone
                </span>
                <span className="font-semibold text-gray-800">
                  {selectedPassenger.mobileNumber || "-"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">
                  Contact Email
                </span>
                <span className="font-semibold text-gray-800 truncate block">
                  {selectedPassenger.emailAddress || "-"}
                </span>
              </div>
            </div>

            {/* User Account Info */}
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <span className="text-gray-400 block text-[10px] uppercase font-bold mb-1">
                Account Owner
              </span>
              {typeof selectedPassenger.user === "object" ? (
                <div>
                  <span className="font-bold text-gray-800 block">
                    {selectedPassenger.user?.username ||
                      selectedPassenger.user?.name ||
                      "Registered User"}
                  </span>
                  <span className="text-gray-500">
                    {selectedPassenger.user?.email || "-"}
                  </span>
                </div>
              ) : (
                <span className="font-mono text-gray-600">
                  ID: {selectedPassenger.user}
                </span>
              )}
            </div>

            <div className="text-[11px] text-gray-400 text-right">
              Record ID: {selectedPassenger._id} • Created:{" "}
              {formatDate(selectedPassenger.createdAt)}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
